import type { LoggerOptions } from 'pino';
import type {
  ApplicationLogEvent,
  CreateApplicationLoggerForBrowser,
  CreateBrowserLoggerConfig,
  DynamicLogEventPropertiesFunction,
} from '@/utils/logger-lib/logger.type';
import { pino } from 'pino';
import { LoggerSensitivityType, LoggingSeverity } from '@/utils/logger-lib/logger.type';
import { getCallerMetadata } from '@/utils/logger-lib/error.util';
import { getPinoPrettyOptions } from '@/utils/logger-lib/pretty-config';
import { createLoggerFunction } from '@/utils/logger-lib/logger-message';
import { getDynamicPropertiesFunction } from '@/utils/logger-lib/logger-dynamic-data';

const createInternalPinoInstanceForBrowser = (
  config: CreateBrowserLoggerConfig & {
    dynamicProperties: DynamicLogEventPropertiesFunction;
  }
) => {
  const { loggingSeverity, applicationName, environment, resolveCaller, loggingEnabled } = config;
  const hostname = 'browser';
  const logEventBase = {
    opVersion: 2,
    application: applicationName,
    environment,
    component: `${applicationName}/${hostname}`,
    sensitivity: LoggerSensitivityType.Tech,
  } satisfies Partial<ApplicationLogEvent>;
  const getCaller = resolveCaller ? getCallerMetadata : () => '';
  const mixin = () => {
    const dynamicData = config.dynamicProperties();

    return {
      requestId: 'n/a',
      logger: getCaller(),
      timestamp: new Date().toISOString(),
      ...dynamicData,
    } satisfies Partial<ApplicationLogEvent>;
  };

  const formatters = {
    level(label: string) {
      const mixinData = mixin();
      return { severity: label.toUpperCase(), ...logEventBase, ...mixinData };
    },
  };

  const pinoOptions: LoggerOptions = {
    // On browser side this MUST match into console.(debug|info|warn|error) or Pino throws a Error.
    level: String(loggingSeverity).toLowerCase(),
    enabled: !!loggingEnabled,
    messageKey: 'message',
    depthLimit: 2,
    timestamp: false,
    // These formatters will be called on server-side.
    formatters,
    browser: {
      asObject: true,
      // These formatters will be called on client-side.
      formatters,
      // This write will be called only on client-side.
      write: (o) => {
        const logEvent = o as ApplicationLogEvent;
        const { text, ...rest } = logEvent.message;
        const args: any[] = [`${logEvent.severity}:`, text];
        if (Object.keys(rest).length > 0) {
          args.push(rest);
        }
        // eslint-disable-next-line no-console
        console.log(...args);
      },
    },
  };
  const prettyOptions = getPinoPrettyOptions();
  const isServerSide = typeof process === 'object';
  const ssrDestination = isServerSide ? process.stdout : undefined;

  return pino({ ...pinoOptions, ...prettyOptions }, ssrDestination);
};

export const createApplicationLoggerForBrowser: CreateApplicationLoggerForBrowser = (config) => {
  const configDefaults: Partial<CreateBrowserLoggerConfig> = {
    loggingSeverity: LoggingSeverity.Info,
    loggingEnabled: true,
    resolveCaller: true,
  };
  if (config.loggingSeverity && !Object.values(LoggingSeverity).includes(config.loggingSeverity)) {
    throw new Error(`Given logging severity level "${config.loggingSeverity}" is not allowed.`);
  }

  const loggerConfig: CreateBrowserLoggerConfig = {
    ...configDefaults,
    ...config,
  };

  const dynamicProperties = getDynamicPropertiesFunction(config.dynamicProperties);

  const logger = createInternalPinoInstanceForBrowser({ ...loggerConfig, dynamicProperties });

  return {
    debug: createLoggerFunction(logger.debug.bind(logger)),
    info: createLoggerFunction(logger.info.bind(logger)),
    warn: createLoggerFunction(logger.warn.bind(logger)),
    error: createLoggerFunction(logger.error.bind(logger)),
  };
};
